import { formatDateTime } from "@/api/datetime";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectStatuses } from "@/features/config/hooks/useConfig";
import { getProjectWorkUnitStats } from "@/features/work-units/api/workUnitHelpers";
import { useCustomerProjects } from "@/features/projects/hooks/useProjects";
import { createFileRoute } from "@tanstack/react-router";
import { Settings, Users } from "lucide-react";
import { HtmlContent } from "@/components/ui/html-content";

export const Route = createFileRoute("/dashboard/customer/projects/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user, loading } = useAuth();
  const { projects, loading: projectsLoading } = useCustomerProjects(user?.uid);
  const { projectStatuses } = useProjectStatuses();
  const navigate = Route.useNavigate();



  if (loading || projectsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">View your assigned projects and track progress</p>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => {
          const workUnitStats = {
            ...getProjectWorkUnitStats(project.workUnits),
            isLegacyMode: !project.workUnits || project.workUnits.length === 0,
          };
          return (
            <Card
              key={project.id}
              className="cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => {
                navigate({
                  to: "/dashboard/customer/projects/$projectId",
                  params: {
                    projectId: project.id,
                  },
                });
              }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      {(() => {
                        const status = projectStatuses.find((s) => s.id === project.statusId);
                        return status ? (
                          <Badge
                            style={{
                              backgroundColor: status.color || "#6b7280",
                              color: "white",
                            }}
                            className="border-0"
                          >
                            {status.name}
                          </Badge>
                        ) : null;
                      })()}
                      <Badge variant="outline" className="flex items-center gap-1">
                        {workUnitStats.isLegacyMode ? (
                          <>
                            <Settings className="h-3 w-3" />
                            Legacy
                          </>
                        ) : (
                          <>
                            <Users className="h-3 w-3" />
                            Work Units
                          </>
                        )}
                      </Badge>
                    </div>
                    <div className="mt-1">
                      <HtmlContent 
                        content={project.description}
                        fallback="No description provided"
                        className="text-sm text-muted-foreground"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {/* Work Units/Workers Summary */}
                  {workUnitStats.isLegacyMode ? (
                    <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-3 text-sm">
                      <Users className="h-4 w-4 text-gray-600" />
                      <span className="font-medium text-gray-700">
                        {workUnitStats.totalAssignedWorkers} worker
                        {workUnitStats.totalAssignedWorkers !== 1 ? "s" : ""} assigned
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-2 rounded-lg bg-blue-50 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                          <Users className="h-4 w-4" />
                          <span>
                            {workUnitStats.totalWorkUnits} Work Unit
                            {workUnitStats.totalWorkUnits !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-blue-900">
                            {workUnitStats.totalActiveWorkers}/{workUnitStats.totalWorkersNeeded}
                          </span>
                          <span className="text-xs text-blue-700">workers</span>
                        </div>
                      </div>
                      {workUnitStats.totalActiveWorkers < workUnitStats.totalWorkersNeeded && (
                        <div className="flex items-center gap-1 text-xs text-red-700">
                          <span>
                            ⚠️ Needs{" "}
                            {workUnitStats.totalWorkersNeeded - workUnitStats.totalActiveWorkers}{" "}
                            more worker
                            {workUnitStats.totalWorkersNeeded -
                              workUnitStats.totalActiveWorkers !==
                            1
                              ? "s"
                              : ""}
                          </span>
                        </div>
                      )}
                      {workUnitStats.totalActiveWorkers === workUnitStats.totalWorkersNeeded &&
                        workUnitStats.totalWorkersNeeded > 0 && (
                          <div className="flex items-center gap-1 text-xs text-green-700">
                            <span>✓ Fully staffed</span>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Work Units/Types Details */}
                  {project.workUnits && project.workUnits.length > 0 && (
                    <div>
                      <div className="mb-2 text-xs font-medium text-gray-600">Work Units:</div>
                      <div className="flex flex-wrap gap-1.5">
                        {project.workUnits.map((workUnit) => {
                          const activeWorkers =
                            workUnit.attachedWorkers?.filter((w) => !w.inactive).length || 0;
                          return (
                            <Badge
                              key={workUnit.id}
                              variant="secondary"
                              className="flex items-center gap-1 text-xs"
                            >
                              <span>{workUnit.name}</span>
                              <span className="text-[10px] opacity-70">
                                ({activeWorkers}/{workUnit.workersNeeded})
                              </span>
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t pt-2">
                    <span className="text-muted-foreground text-xs">
                      Created {formatDateTime(project.createdAt)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {projectsLoading && (
        <div className="py-12 text-center">
          <div className="text-muted-foreground">Loading projects...</div>
        </div>
      )}

      {!projectsLoading && projects.length === 0 && (
        <Card className="py-12 text-center">
          <CardContent className="pt-6">
            <div className="text-muted-foreground mb-2">No projects found</div>
            <p className="text-muted-foreground text-sm">
              You do not have any assigned projects yet
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
